import * as React from 'react';
import { findDOMNode } from 'react-dom';
import cn from 'classnames';
import { Handle, HandleProps, Range, RangeProps } from 'rc-slider';
import Tooltip from 'rc-tooltip';

import 'rc-slider/assets/index.css';
import { Field, FieldProps } from 'formik';
import { useDeepCompareMemoize } from '../search-result-page/hooks';
const _styles = require('./index.scss');

interface RangeSliderHandleProps extends HandleProps {
    showHandleText: boolean;
    formatHandleText?: (value: number, index: number) => string;
    values: number[];
}

interface RangeSliderProps extends RangeProps {
    formikName: string;
    showHandleText: boolean;
    formatHandleText?: (value: number, index: number) => string;
}

/**
 * Handle render function for the rc-slider handles
 *
 * A few notes about the Tooltip instance:
 *
 *  - showHandleText prop is necessary to hide the handles to simplify z-indexing issues
 *  - getTooltipContainer is necessary because we don't want these handles to render on the body
 *    this causes issues with the handle showing when the parent is hidden
 *
 * @param props handle props - NOTE: the typedef package doesn't define a type for the props
 * that are passed to the handle generating function, but it seems to be something that's an extension
 * of the HandleProps type
 */
const handle = (props: RangeSliderHandleProps & any) => {
    const { value, index, values, step, tabIndex, max, min } = props;

    // Need to pop out all the extra props before passing then down to Handle component or we'll
    // get annoying console warnings
    const { dragging, formatHandleText, showHandleText, containerRef, ..._cleanedHandleProps } = props;
    const formatValue = !!props.formatHandleText ? props.formatHandleText(value, index) : value;

    // If the step size is too small, the tooltip handles overlap, to prevent this
    // add css selectors to the tooltip overlay when the overlap hits a threshold
    const steps = (max - min) / step;
    const deltaThreshold = steps / 10; // closer than 10% or range
    const delta = (values[1] - values[0]) / step;
    let nudge = undefined;
    let offset;
    if (delta <= deltaThreshold) {
        offset = (deltaThreshold + 1 - delta) * (10 / deltaThreshold);
        // 10 here is a constant that seems to guarantee
        // that 3-4 char length tooltip are not overlapping
        if (tabIndex === 0) {
            offset = -offset;
        }
    }

    return (
        <div key={index}>
            <Tooltip
                prefixCls={cn(_styles.sliderTooltip)}
                overlayClassName={cn(nudge)}
                overlay={props.showHandleText && formatValue}
                visible={props.showHandleText}
                placement='top'
                key={index}
                align={{
                    offset: [offset, 0],
                    overflow: { adjustX: false } /* by default, if offset causes overflow, tooltip will adjust the offset
                                                   we want to prevent this from happening and allow the overflow
                                                */,
                }}
                getTooltipContainer={props.containerRef ? () => findDOMNode(props.containerRef) as Element : undefined}
            >
                <Handle {..._cleanedHandleProps} />
            </Tooltip>
        </div>
    );
};

const RangeSliderField = (props: { sliderProps: RangeSliderProps; containerRef: HTMLDivElement | null; fieldProps: FieldProps }) => {
    const { sliderProps, containerRef, fieldProps } = props;
    const { field } = fieldProps;
    const { defaultValue, value, ...restSliderProps } = sliderProps;

    const normalizeValues = (values?: number[]) => {
        if (!values) {
            return [sliderProps.min, sliderProps.max];
        }
        return [values[0], values[1] || sliderProps.max];
    };

    let _values = normalizeValues(sliderProps.defaultValue);
    if (value) {
        _values = normalizeValues(value);
    }

    const [minValue, setMinValue] = React.useState(_values[0]);
    const [maxValue, setMaxValue] = React.useState(_values[1]);

    React.useEffect(() => {
        const [low, high] = normalizeValues(field.value);
        setMinValue(low);
        setMaxValue(high);
    }, [useDeepCompareMemoize(field.value)]);

    return (
        <Range
            {...restSliderProps}
            defaultValue={[minValue as number, maxValue as number]}
            value={[minValue as number, maxValue as number]}
            onChange={(value) => {
                const [low, high] = normalizeValues(value);
                setMinValue(low);
                setMaxValue(high);
                const { onChange } = restSliderProps;
                onChange && onChange(value);
            }}
            tabIndex={[0, 1]} // need this so the handle can understand which one it is
            className={cn(_styles.slider, sliderProps.className)}
            handle={(handleProps) =>
                handle({
                    ...handleProps,
                    formatHandleText: sliderProps.formatHandleText,
                    showHandleText: sliderProps.showHandleText,
                    containerRef: containerRef,
                    values: [minValue, maxValue],
                    step: sliderProps.step,
                })
            }
        />
    );
};

const RangeSlider = (props: RangeSliderProps) => {
    // We want the component to re-render when we get the container ref so the handles can be rendered
    // correctly
    const [ref, setRef] = React.useState<HTMLDivElement | null>(null);

    return (
        <div className={_styles.container} ref={setRef}>
            {ref && (
                <Field name={props.formikName}>
                    {(fieldProps: FieldProps) => {
                        return <RangeSliderField sliderProps={props} containerRef={ref} fieldProps={fieldProps} />;
                    }}
                </Field>
            )}
        </div>
    );
};

export default RangeSlider;
